import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { throttle } from 'lodash';

class InlineScroll extends React.Component {
    static propTypes = {
        children: PropTypes.any,
        resetScroll: PropTypes.bool,
        scrollData: PropTypes.object
    };

    static defaultProps = {
        resetScroll: false
    };

    constructor(props, context) {
        super(props, context);

        this._updateCallback = throttle(() => this.forceUpdate(), 50, { 'trailing': false });
        
        this.containerRef = React.createRef();
        this.frameRef = React.createRef();
        this.mutationObserver = null;

        this.state = {
            offset: 0
        };
    }

    componentDidMount() {
        this._updateCallback();
        const container = this.containerRef.current;

        window.addEventListener('resize', this._updateCallback);
        
        if (container && window.MutationObserver) {
            this.mutationObserver = new MutationObserver(this._updateCallback);
            this.mutationObserver.observe(container, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        }

        let { scrollData } = this.props;
        let state = {};

        // Set initial offset when parent wants
        // to scroll to a specific offset in the content
        if ( scrollData && container ) {
            let offsetNode = container.querySelectorAll( scrollData.className );
            state.offset = offsetNode && offsetNode[ scrollData.index ] ? offsetNode[ scrollData.index ].offsetLeft : 0;
        }

        this.setState( state );
    }

    componentWillUnmount() {
        clearTimeout(this._scrollTimeout);
        window.removeEventListener('resize', this._updateCallback);
        
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
    }

    scrollLeft() {
        const container = this.containerRef.current;
        if (!container) return;
        
        const newOffset = this.state.offset - container.clientWidth * 0.9;
        const offset = Math.max(newOffset, 0) || 0;

        this.setState({ offset });
    }

    scrollRight() {
        const container = this.containerRef.current;
        if (!container) return;
        
        const newOffset = this.state.offset + container.clientWidth * 0.9;
        const maxOffset = container.scrollWidth - container.clientWidth;
        const offset = Math.min(newOffset, maxOffset) || 0;

        this.setState({ offset });
    }

    startScrollingLeft(ev) {
        ev.preventDefault();

        let scrolls = 0;

        const scroll = () => {
            scrolls += 1;

            this.scrollLeft();

            const delay = Math.floor(1000 / Math.min(scrolls, 10));
            this._scrollTimeout = setTimeout(scroll, delay);
        };

        scroll();
    }

    startScrollingRight(ev) {
        ev.preventDefault();

        let scrolls = 0;

        const scroll = () => {
            scrolls += 1;

            this.scrollRight();

            const delay = Math.floor(500 / Math.min(scrolls, 5));
            this._scrollTimeout = setTimeout(scroll, delay);
        };

        scroll();
    }

    stopScrolling(ev) {
        ev.preventDefault();
        clearTimeout(this._scrollTimeout);
    }

    render() {
        const container = this.containerRef.current;
        const frame = this.frameRef.current;
        const offset = this.props.resetScroll ? 0 : this.state.offset;
        const containerOffsetRight = container ? Math.floor(container.getBoundingClientRect().right) : 0;
        const frameOffsetRight = frame ? Math.floor(frame.getBoundingClientRect().right) : 0;

        const leftButtonClassNames = classNames('inline-scroll-button left', {
            active: offset > 0
        });

        const rightButtonClassNames = classNames('inline-scroll-button right', {
            active: frameOffsetRight - 20 > containerOffsetRight
        });
        return (
            <div className="inline-scroll">
                <button
                    className={ leftButtonClassNames }
                    onMouseDown={ this.startScrollingLeft.bind(this)}
                    onMouseUp={ this.stopScrolling.bind(this)}
                    onMouseOut={ this.stopScrolling.bind(this)}
                    title="Scroll to left"
                >
                </button>

                <div className="inline-scroll-container" ref={this.containerRef}>
                    <div style={{ transform: `translate(${-offset}px)` }} ref={this.frameRef}>
                       { this.props.children }
                    </div>
                </div>

                <button
                    className={ rightButtonClassNames }
                    onMouseDown={ this.startScrollingRight.bind(this)}
                    onMouseUp={ this.stopScrolling.bind(this)}
                    onMouseOut = { this.stopScrolling.bind(this)}
                    title="Scroll to right"
                >
                </button>
            </div>
        );
    }
}

export default InlineScroll;
